import { useCallback } from 'react';
import { useGetIssueCommand } from '@atlassian/jira-command-palette-common/src/common/ui/command/issue/main.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_LIMIT } from '../constants.tsx';
import { messages } from '../messages.tsx';
import type { GetQuickSearchChildResultProps } from '../types.tsx';
import { getQuickSearchIssueScope, transformQuickSearchIssueResultToIssue } from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';

type UseGetQuickSearchIssueCommands = {
	section: string;
	errorMessage: string;
	showViewAll: boolean;
};

export const useGetQuickSearchIssueCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchIssueCommands) => {
	const { formatMessage } = useIntl();

	const { getIssueCommand } = useGetIssueCommand();
	const { getViewAllCommand } = useGetViewAllCommand();

	const getQuickSearchIssueCommands = useCallback(
		({ error, search, quickSearchResponseData }: GetQuickSearchChildResultProps): Command[] => {
			const issueScope = getQuickSearchIssueScope(quickSearchResponseData);

			// No results
			if (!issueScope || error || issueScope?.error || issueScope?.results?.length === 0) {
				return [];
			}

			// There are results!
			const commands: Command[] = issueScope.results.slice(0, ISSUE_LIMIT).map((issue) => {
				const command = getIssueCommand({
					issue: transformQuickSearchIssueResultToIssue(issue),
					keywords: search,
					section,
					analytics: {
						isSearchResult: true,
					},
				});
				return command;
			});

			if (!showViewAll || issueScope.results.length <= ISSUE_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-issues',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.ISSUE, search)!,
				analyticsPayload: { action: 'viewAllIssues', size: issueScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[formatMessage, getIssueCommand, getViewAllCommand, section, showViewAll],
	);

	return { getQuickSearchIssueCommands };
};
